import { Component, EventEmitter, Output, Inject, PLATFORM_ID, OnInit, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth/auth.service';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import * as fromRoot from '../../app.reducer';
import { CustomIdentity } from '../../models/interfaces/custom_identity.interface';
import { ToastService } from '../../core/services/toast/toast.service';

@Component({
    selector: 'app-google-oauth',
    imports: [],
    templateUrl: './google-oauth.component.html',
    styleUrl: './google-oauth.component.scss'
})
export class GoogleOauthComponent implements OnInit, OnDestroy {
  @Output() authSuccess = new EventEmitter<any>();
  @Output() authError = new EventEmitter<any>();
  @Output() disconnectSuccess = new EventEmitter<any>();
  @Output() disconnectError = new EventEmitter<any>();

  isLoading = false;
  isCheckingStatus = true;
  isConnected = false;
  private popup: Window | null = null;
  private checkClosedTimer: any;
  private destroy$ = new Subject<void>();

  // Observables del estado de autenticación
  isLoggedIn$: Observable<boolean>;
  identity$: Observable<CustomIdentity | null>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private authService: AuthService,
    private store: Store<fromRoot.State>,
    private toastService: ToastService,
  ) {
    this.isLoggedIn$ = this.store.select(fromRoot.getIsLoggedIn);
    this.identity$ = this.store.select(fromRoot.getIdentity);
  }

  ngOnInit(): void {
    this.subscribeToAuthChanges();
    this.checkGoogleConnectionStatus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanup();
  }

  private subscribeToAuthChanges(): void {
    this.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {

        if (!isLoggedIn) {
          this.isConnected = false;
          this.isCheckingStatus = false;
          this.cleanup();
        } else {
          this.checkGoogleConnectionStatus();
        }
      });

    this.identity$
      .pipe(takeUntil(this.destroy$))
      .subscribe(identity => {
        if (identity && this.authService.isLoggedIn()) {
          this.checkGoogleConnectionStatus();
        }
      });
  }

  private checkGoogleConnectionStatus(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.isCheckingStatus = false;
      return;
    }

    const userToken = this.authService.getToken();

    if (!userToken) {
      this.isCheckingStatus = false;
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${userToken}`,
      'Content-Type': 'application/json'
    });

    this.http.get<{success: boolean, isConnected: boolean}>(`${environment.apiBaseUrl}/google-api/connection-status`, { headers })
      .subscribe({
        next: (response) => {
          this.isConnected = response.isConnected;
          this.isCheckingStatus = false;
        },
        error: (error) => {
          this.isConnected = false;
          this.isCheckingStatus = false;
        }
      });
  }

  startGoogleAuth(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.isLoading) return;

    this.isLoading = true;

    try {
      // Obtener el token JWT del usuario autenticado
      const userToken = this.authService.getToken();

      if (!userToken) {
        throw new Error('Usuario no autenticado. Debes iniciar sesión antes de conectar tu cuenta de Google.');
      }

      // Configurar cabeceras con Authorization Bearer
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json'
      });

      // Hacer petición al backend para obtener la URL de Google OAuth
      this.http.get<{authUrl: string}>(`${environment.apiBaseUrl}/google-api/auth`, { headers })
        .subscribe({
          next: (response) => {
            this.openPopupWithAuthUrl(response.authUrl);
          },
          error: (error) => {
            this.handleAuthError(error);
          }
        });

    } catch (error) {
      console.error('Error iniciando autenticación con Google:', error);
      this.handleAuthError(error);
    }
  }

  disconnectFromGoogle(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.isLoading) return;

    this.isLoading = true;

    try {
      const userToken = this.authService.getToken();

      if (!userToken) {
        throw new Error('Usuario no autenticado');
      }

      const headers = new HttpHeaders({
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json'
      });

      this.http.delete(`${environment.apiBaseUrl}/google-api/disconnect`, { headers })
        .subscribe({
          next: (response) => {
            this.isConnected = false;
            this.isLoading = false;
            this.disconnectSuccess.emit(response);
            this.toastService.presentSuccessToast('Cuenta de Google desconectada exitosamente');
          },
          error: (error) => {
            this.isLoading = false;
            this.handleDisconnectError(error);
            this.toastService.presentErrorToast('Error desconectando cuenta de Google');
          }
        });

    } catch (error) {
      console.error('Error desconectando de Google:', error);
      this.isLoading = false;
      this.handleDisconnectError(error);
    }
  }

  private openPopupWithAuthUrl(authUrl: string): void {
    try {
      // Configuración del popup
      const popupWidth = 500;
      const popupHeight = 600;
      const left = (window.screen.width / 2) - (popupWidth / 2);
      const top = (window.screen.height / 2) - (popupHeight / 2);

      const popupFeatures = [
        `width=${popupWidth}`,
        `height=${popupHeight}`,
        `left=${left}`,
        `top=${top}`,
        'resizable=yes',
        'scrollbars=yes',
        'status=yes',
        'menubar=no',
        'toolbar=no',
        'location=no'
      ].join(',');

      // Abrir popup
      this.popup = window.open(authUrl, 'google_oauth', popupFeatures);

      if (!this.popup) {
        throw new Error('No se pudo abrir el popup. Asegúrate de permitir popups para este sitio.');
      }

      // Enfocar el popup
      this.popup.focus();

      // Escuchar mensajes del popup
      this.listenForCallback();

      // Verificar si el popup fue cerrado manualmente
      this.checkIfPopupClosed();

    } catch (error) {
      console.error('Error abriendo popup:', error);
      this.handleAuthError(error);
    }
  }

  private listenForCallback(): void {
    const messageHandler = (event: MessageEvent) => {
      // Verificar el origen por seguridad - permitir múltiples orígenes válidos
      const allowedOrigins = [
        environment.socketUrl,
        environment.baseUrl,
        window.location.origin,
        'http://localhost:3000',
        'http://localhost:4200',
        // staging
        'https://api-staging.academiaenfaf.com',
        'https://staging.academiaenfaf.com',
        // producción
        'https://api.academiaenfaf.com',
        'https://academiaenfaf.com'
      ];

      if (!allowedOrigins.some(origin => event.origin.startsWith(origin))) {
        return;
      }

      if (event.data?.type === 'GOOGLE_OAUTH_SUCCESS') {
        // El backend envía los datos en event.data.data
        this.handleAuthSuccess(event.data.data);
        this.cleanup();
      } else if (event.data?.type === 'GOOGLE_OAUTH_ERROR') {
        // El backend puede enviar el error en event.data.error o directamente en event.data
        const errorData = event.data.error || event.data.data || event.data;
        this.handleAuthError(errorData);
        this.cleanup();
      }
    };

    window.addEventListener('message', messageHandler);

    // Limpiar el listener cuando se complete la autenticación
    const originalCleanup = this.cleanup.bind(this);
    this.cleanup = () => {
      window.removeEventListener('message', messageHandler);
      originalCleanup();
    };
  }

  private checkIfPopupClosed(): void {
    this.checkClosedTimer = setInterval(() => {
      if (this.popup?.closed) {
        this.cleanup();
      }
    }, 1000);
  }

  private handleAuthSuccess(data: any): void {
    this.isConnected = true;
    this.authSuccess.emit(data);
    this.toastService.presentSuccessToast('Autenticación con Google exitosa');
  }

  private handleAuthError(error: any): void {
    this.authError.emit(error);
    this.toastService.presentErrorToast('Error autenticando con Google');
  }

  private handleDisconnectError(error: any): void {
    this.disconnectError.emit(error);
    this.toastService.presentErrorToast('Error desconectando cuenta de Google');
  }

  private cleanup(): void {
    this.isLoading = false;

    if (this.checkClosedTimer) {
      clearInterval(this.checkClosedTimer);
      this.checkClosedTimer = null;
    }

    if (this.popup && !this.popup.closed) {
      this.popup.close();
    }

    this.popup = null;
  }
}
